import React, { useState, useEffect, useContext } from "react";
import toast from "react-hot-toast";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Card,
  Modal,
  Alert, // Added Alert for error display
  Spinner, // Added Spinner for loading display
} from "react-bootstrap";
import {
  FaPlus,
  FaMinus,
  FaTrash,
  FaArrowLeft,
  FaWhatsapp,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import "./orderpage.css";
import { CartContext } from "./CartContext";
import { v4 as uuidv4 } from "uuid";
// NOTE: Removed 'websiteDetails' import as we are fetching data dynamically
import API_DOMAIN from "../config/config";
const OrderPage = () => {
  const {
    cart,
    increaseQty,
    decreaseQty,
    removeItem,
    updateQty,
    clearCart,
    orderMode,
    setOrderMode,
    userDetails,
    setUserDetails,
  } = useContext(CartContext);

  // ----------- FORM STATE & VALIDATION -----------
  const [name, setName] = useState(userDetails.name || "");
  const [phone, setPhone] = useState(userDetails.phone || "");
  const [address, setAddress] = useState(userDetails.address || "");
  const [tableNo, setTableNo] = useState(userDetails.tableNo || "");
  const [phoneError, setPhoneError] = useState("");

  useEffect(() => {
    if (userDetails) {
      setName(userDetails.name || "");
      setPhone(userDetails.phone || "");
      setAddress(userDetails.address || "");
      setTableNo(userDetails.tableNo || "");
    }
  }, [userDetails]);

  // ----------- API & MODAL STATE -----------
  const [showPopup, setShowPopup] = useState(false);
  const [orderId, setOrderId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companyDetails, setCompanyDetails] = useState(null); // New state for dynamic company info
  console.log("Company Details:", companyDetails);
  const [companyLoading, setCompanyLoading] = useState(true);
  const [websiteSettings, setWebsiteSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);

  const navigate = useNavigate();

  // ------------------ HANDLERS ------------------

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    // Only allow digits and limit length to 10
    const filteredValue = value.replace(/\D/g, "").slice(0, 10);
    setPhone(filteredValue);

    // Clear error immediately if it meets 10 digits or is empty
    if (filteredValue.length === 10 || filteredValue.length === 0) {
      setPhoneError("");
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const closePopup = () => {
    setShowPopup(false);
    setName("");
    setPhone("");
    setAddress("");
    setTableNo("");
    setOrderMode(null);
    navigate("/");
  };

  // ------------------ DATA FETCHING (Company Details + Products) ------------------

  useEffect(() => {
    const fetchCompanyAndProducts = async () => {
      // 1. Fetch Company Details
      setCompanyLoading(true);
      setSettingsLoading(true);
      try {
        const companyResponse = await fetch(`${API_DOMAIN}/company.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "list" }),
        });

        const companyData = await companyResponse.json();
        if (
          companyData.status === 200 &&
          companyData.data &&
          companyData.data.length > 0
        ) {
          setCompanyDetails(companyData.data[0]);
        } else {
          console.error("Failed to load company details:", companyData.msg);
        }
      } catch (e) {
        console.error("Error fetching company:", e);
      } finally {
        setCompanyLoading(false);
      }

      try {
        const settingsResponse = await fetch(
          `${API_DOMAIN}/website_settings.php`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ action: "getSettings" }),
          },
        );
        const settingsData = await settingsResponse.json();
        if (settingsData.head.code === 200) {
          setWebsiteSettings(settingsData.body.settings);
        }
      } catch (e) {
        console.error("Error fetching website settings:", e);
      } finally {
        setSettingsLoading(false);
      }

      setLoading(true);
      try {
      } catch (e) {
        // handle error if necessary
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyAndProducts();
  }, []);

  // ------------------ ORDER CALCULATION ------------------
  const subTotal = cart.reduce((sum, item) => sum + item.price * item.qty, 0);
  const discount = 0;
  const total = subTotal - discount;

  // Delivery Charge Logic:
  // "delivery_charge_enabled" value 0 va erunthu "delivery_charge" value 0 va ilama eruntha
  // other_charges la save akanum.
  let otherCharges = 0;
  if (
    websiteSettings &&
    Number(websiteSettings.delivery_charge_enabled) === 0 &&
    Number(websiteSettings.delivery_charge) > 0 &&
    orderMode === 3 // Only apply for Home Delivery
  ) {
    otherCharges = Number(websiteSettings.delivery_charge);
  }

  // GST Logic:
  // gst_enabled 0 va eruntha defaut 5% gst add akanum.
  // gst_type la GST@5% and gst_amount athoda value save akanum.
  let gstAmount = 0;
  let gstType = "";
  if (websiteSettings && Number(websiteSettings.gst_enabled) === 0) {
    gstType = "GST@5%";
    gstAmount = total * 0.05;
  }

  const grandTotal = total + otherCharges + gstAmount;

  const totalQuantity = cart.length;

  // ------------------ PLACE ORDER LOGIC ------------------
  const handlePlaceOrder = async (e) => {
    e.preventDefault();

    // 1. Validate order mode is selected
    if (orderMode === null) {
      toast.error(
        "Please select an order type (Table / Walking Customer / Home Delivery).",
      );
      return;
    }

    // 2. Basic field validation
    if (cart.length === 0 || !name) {
      toast.error(
        "Please fill in your name and ensure your cart is not empty.",
      );
      return;
    }

    // 3. Table mode needs table number
    if (orderMode === 0 && !tableNo) {
      toast.error("Please enter the Table Number.");
      return;
    }

    // 4. Home delivery needs address
    if (orderMode === 3 && !address) {
      toast.error("Please enter your delivery address.");
      return;
    }

    // 2. Phone Validation Check (10 digits)
    if (phone.length !== 10) {
      setPhoneError("Mobile number must be exactly 10 digits.");
      return;
    }
    setPhoneError(""); // Clear error if validation passes

    setIsSubmitting(true);
    setError(null);

    const dynamicCompanyId = companyDetails?.company_id || "";
    console.log("Using Company ID for Order:", dynamicCompanyId);
    const orderPayload = {
      company_id: dynamicCompanyId,
      order_date: new Date().toISOString().split("T")[0],
      order_no: `ORD-${uuidv4().slice(0, 6).toUpperCase()}`,
      order_mode: orderMode,
      table_no: orderMode === 0 ? tableNo : "",
      customer_details: JSON.stringify({
        name: name,
        phone: phone,
        address: orderMode === 3 ? address : "",
        table_no: orderMode === 0 ? tableNo : "",
      }),
      product_details: JSON.stringify(
        cart.map((item) => {
          return {
            product_id: item.product_id,
            product_name: item.product_name,
            barcode: item.barcode || "",
            purchase_rate: (item.purchase_rate || 0).toFixed(2),
            sales_rate: (item.sales_rate || 0).toFixed(2),
            discount_percent: (item.discount_percent || 0).toFixed(2),
            discount_amount: (item.discount_amount || 0).toFixed(2),
            sales_rate_with_discount_amount: (item.sales_rate_with_discount_amount || 0).toFixed(2),
            price: item.price.toFixed(2),
            qty: item.qty,
            line_total: (item.price * item.qty).toFixed(2),
          };
        }),
      ),
      sub_total: subTotal.toFixed(2),
      discount: discount.toFixed(2),
      total: total.toFixed(2),
      gst_type: gstType,
      gst_amount: gstAmount.toFixed(2),
      other_charges: otherCharges.toFixed(2),
      grand_total: grandTotal.toFixed(2),
      user_id: 1,
      created_name: "Customer Order",
    };

    try {
      const response = await fetch(`${API_DOMAIN}/order.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(orderPayload),
      });

      console.log("Order API orderPayload:", orderPayload);

      const data = await response.json();

      if (data.head.code === 200) {
        const apiOrderId = data.body.order_no || orderPayload.order_no;
        setOrderId(apiOrderId);
        clearCart();
        setShowPopup(true);
      } else {
        throw new Error(
          data.head.msg || "Order creation failed on the server.",
        );
      }
    } catch (e) {
      console.error("Order API Error:", e);

      toast.error(`${e.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const mobileFromCompany = String(companyDetails?.mobile_number || "");
  const companyName = companyDetails?.company_name || "Online Store";
  const contactNumber = mobileFromCompany || "N/A";
  const cleanedContactNumber = mobileFromCompany.replace(/[^\d]/g, "");
  const whatsappLink = `https://wa.me/91${cleanedContactNumber}`;

  if (companyLoading && !companyDetails) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" />
        <p>Loading order details...</p>
      </Container>
    );
  }

  return (
    <div className="order-page-wrapper">
      <Container fluid className="py-4">
        <Row className="justify-content-center">
          <Col md={10}>
            <Row className="mb-3">
              <Col>
                <Button
                  variant="link"
                  onClick={handleGoBack}
                  className="p-2 text-white bg-primary"
                  style={{ fontSize: "1rem" }}
                >
                  <FaArrowLeft className="me-2" />
                </Button>
              </Col>
            </Row>
            <Row>
              <Col md={8}>
                <Card className="mb-3 p-3 custom-card">
                  <Row>
                    <Col md={8}>
                      <h5>
                        <b>Order Summary</b>
                      </h5>
                    </Col>
                    <Col md={4} className="text-end">
                      <p className="item-count">
                        {totalQuantity > 0
                          ? `${totalQuantity} items`
                          : "0 items"}
                      </p>
                    </Col>
                  </Row>
                  <hr className="summary-divider" />
                  {cart.length === 0 ? (
                    <p>Your cart is empty.</p>
                  ) : (
                    <div className="order-summary-items">
                      {cart.map((item) => (
                        <Row
                          key={item.id}
                          className="align-items-start mb-3 product-item"
                        >
                          {/* Image */}
                          <Col xs={3} sm={2}>
                            <img
                              src={
                                item.product_img_url ||
                                "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcT7r0TBgU8-SuHKlgfTmNzdQoMrk3nU5tFarg&s"
                              }
                              alt={item.product_name}
                              className="order-product-img"
                            />
                          </Col>

                          {/* Name + Price + Remove + Qty (all on right on mobile) */}
                          <Col xs={9} sm={10}>
                            <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                              <div>
                                <h6 className="mb-1">{item.product_name}</h6>
                                <strong>
                                  ₹ {item.price.toFixed(2)}{" "}
                                  <span className="text-muted small fw-normal">
                                    / {item.unit || "unit"}
                                  </span>
                                </strong>

                                <div>
                                  <Button
                                    variant="link"
                                    className="text-danger p-0 mt-1 remove-btn"
                                    onClick={() => removeItem(item.id)}
                                  >
                                    <FaTrash /> Remove
                                  </Button>
                                </div>
                              </div>
                              <div className="quantity-controls-inline">
                                <button
                                  className="qty-btn-sm"
                                  onClick={() => decreaseQty(item.id)}
                                >
                                  −
                                </button>
                                <input
                                  type="number"
                                  step="any"
                                  className="qty-input-sm"
                                  value={item.qty}
                                  onChange={(e) =>
                                    updateQty(item.id, e.target.value)
                                  }
                                />
                                <button
                                  className="qty-btn-sm"
                                  onClick={() => increaseQty(item.id)}
                                >
                                  +
                                </button>
                              </div>
                            </div>
                          </Col>
                        </Row>
                      ))}
                    </div>
                  )}
                </Card>
              </Col>

              {/* Order Info Summary */}
              <Col md={4}>
                {orderMode !== null && (
                  <Card className="mb-3 p-3 custom-card shadow-sm border-0">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0 fw-bold text-primary">
                        Order Details
                      </h6>
                    </div>

                    <div className="order-info-summary">
                      <p className="mb-1 small">
                        <strong>Order Type:</strong>{" "}
                        {orderMode === 0
                          ? "Table"
                          : orderMode === 1
                            ? "Walking"
                            : "Delivery"}
                      </p>
                      <p className="mb-1 small">
                        <strong>Customer:</strong> {name}
                      </p>
                      <p className="mb-1 small">
                        <strong>Contact:</strong> {phone}
                      </p>
                      {orderMode === 0 && (
                        <p className="mb-1 small">
                          <strong>Table No:</strong> {tableNo}
                        </p>
                      )}
                      {orderMode === 3 && (
                        <p className="mb-0 small text-truncate">
                          <strong>Address:</strong> {address}
                        </p>
                      )}
                    </div>
                  </Card>
                )}

                {/* Price Details */}
                <Card className="mb-3 p-3 custom-card">
                  <h6>
                    <b>Price Details</b>
                  </h6>
                  <div className="price-row">
                    <span>Sub Total</span>
                    <span>₹ {subTotal.toFixed(2)}</span>
                  </div>
                  {discount > 0 && (
                    <div className="price-row">
                      <span>Discount</span>
                      <span>- ₹ {discount.toFixed(2)}</span>
                    </div>
                  )}

                  {gstAmount > 0 && (
                    <div className="price-row">
                      <span>GST (5%)</span>
                      <span>₹ {gstAmount.toFixed(2)}</span>
                    </div>
                  )}
                  {otherCharges > 0 && (
                    <div className="price-row">
                      <span>Delivery Charge</span>
                      <span>₹ {otherCharges.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="price-total">
                    <span>To Pay</span>
                    <span>₹ {grandTotal.toFixed(2)}</span>
                  </div>
                  {/* <div
                    style={{
                      color: "green",
                      fontSize: "12px",
                      marginTop: "5px",
                    }}
                  >
                    Add items worth ₹ {Math.max(845 - subTotal, 0).toFixed(2)}{" "}
                    more to place an order
                  </div> */}
                </Card>

                <Button
                  className="btn-place-order w-100"
                  onClick={handlePlaceOrder}
                  disabled={
                    cart.length === 0 ||
                    orderMode === null ||
                    !name ||
                    !phone ||
                    (orderMode === 0 && !tableNo) ||
                    (orderMode === 3 && !address) ||
                    isSubmitting
                  }
                >
                  {isSubmitting ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Placing Order...
                    </>
                  ) : (
                    <>
                      Place Order <br />
                      <span style={{ fontSize: "12px" }}>
                        COD (Cash On Delivery)
                      </span>
                    </>
                  )}
                </Button>
                {error && (
                  <Alert variant="danger" className="mt-3">
                    {error}
                  </Alert>
                )}
              </Col>
            </Row>
          </Col>
        </Row>

        {/* Popup for Order Confirmation (DYNAMICALLY UPDATED) */}
        <Modal show={showPopup} onHide={closePopup} centered>
          <Modal.Header closeButton>
            <Modal.Title></Modal.Title>
          </Modal.Header>
          <Modal.Body className="popup-content">
            <img
              src="https://vyaparwebsiteimages.vypcdn.in/catalogue/done-large.svg?w=64&q=75"
              alt="Tick Icon"
              className="tick-icon"
            />
            <h4 className="order-placed">Order Placed!</h4>
            <p className="order-id">Your Order ID is {orderId}.</p>
            <p className="order-success">
              Your order was placed successfully from <b>{companyName}</b>
            </p>

            {/* <Row className="contact-row">
              <Col xs={6} className="contact-col">
                <p className="contact-box">
                  <a
                    href={`tel:${contactNumber}`}
                    className="contact-link phone"
                  >
                    📞 {contactNumber}
                  </a>
                </p>
              </Col>
              <Col xs={6} className="contact-col">
                <p className="contact-box">
                  <a
                    href={whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-link whatsapp"
                  >
                    <FaWhatsapp size={20} color="#25D366" /> Send WhatsApp
                  </a>
                </p>
              </Col>
            </Row> */}
            {/* <hr className="popup-divider" />
            <p className="popup-footer-text">
              <b>{companyName}</b> and 1 Lakh+ users are using our site to get
              orders online.
            </p> */}
            {/* <Button
              variant="danger"
              className="w-50"
              onClick={closePopup}
              style={{ backgroundColor: "#ff0000", borderColor: "#ff0000" }}
            >
              Download Vyapar App
            </Button> */}
          </Modal.Body>
        </Modal>
      </Container>
    </div>
  );
};

export default OrderPage;
